import { useEffect, useMemo, useState } from "react";
import { Gift, Pencil, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { ReferralShareActions } from "@/components/referrals/ReferralShareActions";

export const ReferralHub = () => {
  const { addReferralReward } = useCart();
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [code, setCode] = useState("");
  const [editing, setEditing] = useState(false);

  const load = async () => {
    const [{ data: referralData }, { data: productData }] = await Promise.all([
      api.get("/referrals/me"),
      api.get("/products", { params: { available_only: true } }),
    ]);
    setData(referralData);
    setProducts(productData);
  };

  useEffect(() => {
    load().catch(() => {});
  }, []);

  const rewardProducts = useMemo(() => {
    if (!data) return [];
    return products.filter((product) => data.reward_slugs.includes(product.slug));
  }, [data, products]);

  const changeCode = async () => {
    try {
      await api.put("/referrals/code", { code });
      toast.success("Your referral code is updated. Your old links still work.");
      setEditing(false);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Please pick another code.");
    }
  };

  if (!data) return null;
  const needed = data.settings.referrals_per_reward - data.progress;

  return (
    <section data-testid="my-referrals-section" className="mt-10 border-t border-line pt-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-xs text-espresso-light">My referrals</div>
          <h2 className="mt-2 font-serif text-3xl text-espresso">Share a spoonful of joy.</h2>
        </div>
        <div className="flex items-center gap-2 text-sm text-espresso-light">
          <Users size={16} strokeWidth={1.5} />
          Give 10%, earn a free cup every {data.settings.referrals_per_reward} friends.
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
        <div className="border border-line bg-ivory p-6 md:p-8">
          <div className="label-xs text-espresso-light">Your referral code</div>
          <div data-testid="referral-code-value" className="mt-3 font-serif text-4xl text-espresso">
            {data.code}
          </div>
          <div className="mt-3 break-all text-xs text-espresso-light">
            {window.location.origin}/?ref={data.code}
          </div>
          <ReferralShareActions code={data.code} />
          {!data.code_changed && !editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              data-testid="change-referral-code-button"
              className="mt-5 inline-flex items-center gap-2 text-xs text-espresso underline underline-offset-4"
            >
              <Pencil size={13} strokeWidth={1.5} /> Change code once
            </button>
          )}
          {editing && (
            <div className="mt-5 flex flex-wrap gap-3" data-testid="change-referral-code-form">
              <input
                value={code}
                onChange={(event) => setCode(event.target.value.toUpperCase())}
                data-testid="custom-referral-code-input"
                maxLength={15}
                placeholder="4–15 letters or numbers"
                className="min-w-0 flex-1 border-b border-espresso bg-transparent px-0 py-2 text-sm uppercase outline-none"
              />
              <button onClick={changeCode} data-testid="save-referral-code-button" className="btn-espresso px-4">
                Save
              </button>
            </div>
          )}
        </div>

        <div className="border border-line bg-espresso p-6 text-ivory md:p-8">
          <div className="flex items-center gap-2 text-gold"><Gift size={16} /> Free cup progress</div>
          <div className="mt-5 h-2 overflow-hidden bg-ivory/20">
            <div
              data-testid="referral-progress-bar"
              className="h-full bg-gold transition-[width]"
              style={{ width: `${(data.progress / data.settings.referrals_per_reward) * 100}%` }}
            />
          </div>
          <p data-testid="referral-progress-copy" className="mt-4 font-serif text-2xl leading-tight">
            {data.progress} of {data.settings.referrals_per_reward} friends have ordered — {needed} more to your free cup!
          </p>
        </div>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-2">
        <div>
          <div className="label-xs text-espresso-light">Your friends</div>
          <div className="mt-3 divide-y divide-line border-y border-line">
            {data.referrals.length === 0 ? (
              <p className="py-4 text-sm text-espresso-light">Your shared links will appear here.</p>
            ) : data.referrals.map((referral) => (
              <div key={referral.order_number} className="flex items-center justify-between py-3 text-sm">
                <span>{referral.friend_first_name}</span>
                <span data-testid={`referral-status-${referral.order_number}`} className="label-xs text-espresso-light">
                  {referral.status === "counted" ? "Counted" : "Pending"}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="label-xs text-espresso-light">Free cups</div>
          <div className="mt-3 space-y-3">
            {data.rewards.length === 0 ? (
              <p className="text-sm text-espresso-light">Your earned free cups will appear here.</p>
            ) : data.rewards.map((reward) => (
              <div key={reward.id} className="border border-line p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-serif text-xl text-espresso">150g tiramisu cup</div>
                    <div className="mt-1 text-xs text-espresso-light">
                      {reward.status} · expires {new Date(reward.expires_at).toLocaleDateString("en-IN")}
                    </div>
                  </div>
                </div>
                {reward.status === "available" && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {rewardProducts.map((product) => (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => addReferralReward(product, reward.id)}
                        data-testid={`add-referral-reward-${reward.id}-${product.slug}`}
                        className="border border-espresso px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-espresso hover:bg-espresso hover:text-ivory"
                      >
                        {product.name.replace(" Cup", "")}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};